import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Key, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import api from '../services/api';

const Profile = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    // Only show password change if user is manual (has no googleId/microsoftId in theory, 
    // but in this mock environment we check if they log in via email)
    const canChangePassword = true; // In this mock-up we allow it, backend will verify if password exists

    const handleChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        setLoading(true);
        setMessage(null);

        try {
            await api.post('/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            setMessage({ type: 'success', text: 'Password changed successfully!' });
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to change password' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container section">
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-muted)', marginBottom: '1.5rem' }}
                >
                    <ArrowLeft size={18} /> Back to Home
                </button>

                <div className="glass-card" style={{ padding: '2rem' }}>
                    <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                        <div style={{
                            width: '80px', height: '80px', borderRadius: '50%', background: 'var(--primary-light)',
                            color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 1rem'
                        }}>
                            <User size={40} />
                        </div>
                        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{user.name}</h1>
                        <p style={{ color: 'var(--text-muted)' }}>{user.email}</p>
                        <span style={{
                            background: '#f3f4f6', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
                            color: '#4b5563', textTransform: 'uppercase'
                        }}>
                            {user.role}
                        </span>
                    </div>

                    <div style={{ borderTop: '1px solid var(--border)', paddingTop: '2rem' }}>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Lock size={20} /> Security Settings
                        </h2>

                        {user.isSSO ? (
                            <div style={{
                                padding: '1.5rem',
                                borderRadius: '0.5rem',
                                background: '#f0f9ff',
                                border: '1px solid #bae6fd',
                                color: '#0369a1',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '0.75rem'
                            }}>
                                <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '2px' }} />
                                <div>
                                    <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>SSO Managed Account</p>
                                    <p style={{ fontSize: '0.9rem', opacity: 0.9 }}>
                                        Your account is managed by your Organization's Single Sign-On (Google/Microsoft/SAML). 
                                        Please contact your IT administrator to change your password or security settings.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {message && (
                                    <div style={{
                                        padding: '1rem', borderRadius: '0.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem',
                                        backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
                                        color: message.type === 'success' ? '#166534' : '#991b1b',
                                        border: `1px solid ${message.type === 'success' ? '#bbf7d0' : '#fecaca'}`
                                    }}>
                                        {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
                                        {message.text}
                                    </div>
                                )}

                                <form onSubmit={handlePasswordChange} style={{ display: 'grid', gap: '1.25rem' }}>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Current Password</label>
                                        <input
                                            type="password"
                                            name="currentPassword"
                                            value={passwordData.currentPassword}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>New Password</label>
                                        <input
                                            type="password"
                                            name="newPassword"
                                            value={passwordData.newPassword}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                            minLength={6}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem', fontSize: '0.9rem' }}>Confirm New Password</label>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            value={passwordData.confirmPassword}
                                            onChange={handleChange}
                                            className="form-input"
                                            required
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={loading}
                                        style={{ width: '100%', marginTop: '0.5rem' }}
                                    >
                                        <Key size={18} style={{ marginRight: '0.5rem' }} />
                                        {loading ? 'Updating...' : 'Change Password'}
                                    </button>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
